// src/data/amazonCalculator.js
// Pure calculation helpers for the Amazon FBA/FBM profit calculator.
// No React, no Firebase — fully unit-testable.

import { REFERRAL_FEES, FBA_SIZE_TIERS, STORAGE_FEES } from './amazonFees';

const num = (v) => (typeof v === 'number' && isFinite(v) ? v : 0);

export function lookupReferralFee(categoryId, salePrice) {
  const category = REFERRAL_FEES.find((c) => c.id === categoryId);
  if (!category) return { rate: 0, fee: 0 };

  const price = num(salePrice);
  let rate;
  if (category.tieredRate) {
    const tier = category.tieredRate.find((t) => price < t.maxPrice);
    rate = tier ? tier.rate : category.tieredRate[category.tieredRate.length - 1].rate;
  } else {
    rate = category.rate;
  }

  const minFee = category.minFee || 0;
  const fee = Math.max(price * rate, minFee);
  return { rate, fee };
}

export function lookupFBAFee(sizeTierId, weightOz) {
  const tier = FBA_SIZE_TIERS.find((t) => t.id === sizeTierId);
  if (!tier) return 0;
  const oz = num(weightOz);
  const band = tier.fees.find((b) => oz <= b.maxOz);
  return band ? band.fee : tier.fees[tier.fees.length - 1].fee;
}

export function calculateStorageFee(sizeTierId, dimensions, storageMonths, month = new Date().getMonth()) {
  const tier = FBA_SIZE_TIERS.find((t) => t.id === sizeTierId);
  if (!tier) return 0;

  const L = num(dimensions?.length);
  const W = num(dimensions?.width);
  const H = num(dimensions?.height);
  const months = num(storageMonths);
  if (L <= 0 || W <= 0 || H <= 0 || months <= 0) return 0;

  const cubicFeet = (L * W * H) / 1728;
  const rates = STORAGE_FEES[tier.storageType];
  // month is 0-indexed: Oct=9, Nov=10, Dec=11
  const rate = month >= 9 ? rates.octDec : rates.janSep;
  return cubicFeet * rate * months;
}

function resolvePpcCost(inputs) {
  const { ppcMode, ppcFlat, ppcAcos, salePrice } = inputs;
  if (ppcMode === 'acos') return num(salePrice) * num(ppcAcos);
  return num(ppcFlat);
}

function buildResult({ mode, inputs, fulfillmentFee, shippingCost, storageFee, referralInfo, ppcCost }) {
  const salePrice = num(inputs.salePrice);
  const productCost = num(inputs.productCost);
  const miscCost = num(inputs.miscCost);

  const referralFee = referralInfo.fee;
  const totalFees = referralFee + fulfillmentFee + storageFee + shippingCost + ppcCost + miscCost;
  const netProfit = salePrice - productCost - totalFees;

  const hasRevenue = salePrice > 0;
  const investedCapital = productCost + (mode === 'fba' ? num(inputs.inboundShipping) : 0) + miscCost;

  const margin = hasRevenue ? netProfit / salePrice : null;
  const roi = investedCapital > 0 ? netProfit / investedCapital : null;

  const acosRate = inputs.ppcMode === 'acos' ? num(inputs.ppcAcos) : 0;
  const ppcFixed = inputs.ppcMode === 'flat' ? num(inputs.ppcFlat) : 0;
  const fixedCosts = productCost + fulfillmentFee + storageFee + shippingCost + miscCost + ppcFixed;
  const denominator = 1 - referralInfo.rate - acosRate;
  const breakEvenPrice = denominator > 0 ? fixedCosts / denominator : null;

  return {
    mode,
    salePrice,
    productCost,
    referralFee,
    fulfillmentFee,
    storageFee,
    shippingCost,
    ppcCost,
    miscCost,
    totalFees,
    netProfit,
    margin,
    roi,
    breakEvenPrice,
  };
}

export function calculateFBA(inputs) {
  const fulfillmentFee = lookupFBAFee(inputs.sizeTierId, inputs.weightOz);
  const shippingCost = num(inputs.inboundShipping);
  const storageFee = calculateStorageFee(inputs.sizeTierId, inputs.dimensions, inputs.storageMonths);
  const referralInfo = lookupReferralFee(inputs.categoryId, inputs.salePrice);
  const ppcCost = resolvePpcCost(inputs);

  return buildResult({
    mode: 'fba',
    inputs,
    fulfillmentFee,
    shippingCost,
    storageFee,
    referralInfo,
    ppcCost,
  });
}

export function calculateFBM(inputs) {
  const shippingCost = num(inputs.outboundShipping);
  const referralInfo = lookupReferralFee(inputs.categoryId, inputs.salePrice);
  const ppcCost = resolvePpcCost(inputs);

  return buildResult({
    mode: 'fbm',
    inputs,
    fulfillmentFee: 0,
    shippingCost,
    storageFee: 0,
    referralInfo,
    ppcCost,
  });
}

export function calculateCompare(inputs) {
  return {
    fba: calculateFBA(inputs),
    fbm: calculateFBM(inputs),
  };
}

// Given product cost, fees, and a desired profit, solve for the sale price needed.
// Handles tiered referral categories by trying each tier and returning the one
// whose resulting price actually falls within that tier (iterative stability).
//
// Does not account for the minimum referral fee floor (usually $0.30) — at the
// sale prices this function produces (usually >$5), the min fee rarely binds.
export function calculateTargetPrice(inputs, targetProfit, mode = 'fba') {
  const target = num(targetProfit);
  const productCost = num(inputs.productCost);
  const miscCost = num(inputs.miscCost);
  const acosRate = inputs.ppcMode === 'acos' ? num(inputs.ppcAcos) : 0;
  const ppcFixed = inputs.ppcMode === 'flat' ? num(inputs.ppcFlat) : 0;

  let fulfillmentFee = 0;
  let shippingCost = 0;
  let storageFee = 0;
  if (mode === 'fba') {
    fulfillmentFee = lookupFBAFee(inputs.sizeTierId, inputs.weightOz);
    shippingCost = num(inputs.inboundShipping);
    storageFee = calculateStorageFee(inputs.sizeTierId, inputs.dimensions, inputs.storageMonths);
  } else {
    shippingCost = num(inputs.outboundShipping);
  }

  const fixedCosts = productCost + fulfillmentFee + storageFee + shippingCost + miscCost + ppcFixed + target;

  const category = REFERRAL_FEES.find((c) => c.id === inputs.categoryId);
  if (!category) return null;

  const solveWithRate = (rate) => {
    const denom = 1 - rate - acosRate;
    if (denom <= 0) return null;
    return fixedCosts / denom;
  };

  // Flat rate category — single closed-form solve.
  if (!category.tieredRate) {
    return solveWithRate(category.rate);
  }

  // Tiered category — try each tier in order, return the price that's consistent
  // with the rate it was computed at. Tiers are assumed ascending by maxPrice.
  for (let i = 0; i < category.tieredRate.length; i++) {
    const tier = category.tieredRate[i];
    const prevMax = i > 0 ? category.tieredRate[i - 1].maxPrice : 0;
    const price = solveWithRate(tier.rate);
    if (price === null) continue;
    if (price >= prevMax && price < tier.maxPrice) {
      return price;
    }
  }

  // Fallback: no tier was self-consistent (rare). Use the highest-tier rate.
  const lastTier = category.tieredRate[category.tieredRate.length - 1];
  return solveWithRate(lastTier.rate);
}

export const DEFAULT_INPUTS = {
  productCost: '',
  salePrice: '',
  categoryId: 'home',
  sizeTierId: 'small_standard',
  weightOz: '',
  dimensions: { length: '', width: '', height: '' },
  inboundShipping: '',
  outboundShipping: '',
  storageMonths: 1,
  miscCost: '',
  ppcMode: 'flat',
  ppcFlat: '',
  ppcAcos: '',
  targetProfit: '',
};

// Convert form inputs (which may be strings from text fields) into numeric inputs for calc.
export function normalizeInputs(raw) {
  const toNum = (v) => {
    if (v === '' || v === null || v === undefined) return 0;
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };
  return {
    productCost: toNum(raw.productCost),
    salePrice: toNum(raw.salePrice),
    categoryId: raw.categoryId,
    sizeTierId: raw.sizeTierId,
    weightOz: toNum(raw.weightOz),
    dimensions: {
      length: toNum(raw.dimensions?.length),
      width: toNum(raw.dimensions?.width),
      height: toNum(raw.dimensions?.height),
    },
    inboundShipping: toNum(raw.inboundShipping),
    outboundShipping: toNum(raw.outboundShipping),
    storageMonths: toNum(raw.storageMonths),
    miscCost: toNum(raw.miscCost),
    ppcMode: raw.ppcMode || 'flat',
    ppcFlat: toNum(raw.ppcFlat),
    ppcAcos: toNum(raw.ppcAcos),
    targetProfit: toNum(raw.targetProfit),
  };
}
